import { forEach } from 'lodash';

import RESTAPIError from '../../interfaces/RESTAPIError';

export function isRESTAPIError(x: unknown): x is RESTAPIError {
  if (
    x &&
    typeof x === 'object' &&
    (('code' in x && 'message' in x && 'response' in x) ||
      ('status' in x && 'message' in x))
  ) {
    return true;
  }
  return false;
}

export function getFileExtension(filename: string) {
  return filename.slice(
    (Math.max(0, filename.lastIndexOf('.')) || Infinity) + 1,
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function setS3UploadFormData(requestBody: Record<string, any>) {
  const requestBodyFormData = new FormData();
  forEach(requestBody, (value, key) => {
    requestBodyFormData.append(key, value);
  });
  return requestBodyFormData;
}

export function getConfideFormattedDateTime(dateString: string) {
  return new Date(dateString).toLocaleString(import.meta.env.VITE_LOCALE, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  });
}

export const convertLinkToString = (
  link: string | undefined | null,
): string => {
  if (!link) return 'No Page information';
  const newlink = link.replace(/^\/|\/$/g, '');
  const strings = newlink.split('/');
  const result = strings.join(' - ');
  return result;
};

export function bytesToGB(bytes: number) {
  if (typeof bytes !== 'number' || isNaN(bytes) || bytes < 0) {
    return bytes;
  }

  const gigabytes = bytes / (1024 * 1024 * 1024);
  return gigabytes.toFixed(2) + ' GB';
}

export function convertToBytes(value: string) {
  if (value.endsWith(' GB')) {
    const newValue = value.replace(' GB', '');
    const bytes = parseFloat(newValue) * (1024 * 1024 * 1024);
    return bytes.toFixed(0);
  } else if (value.endsWith(' MB')) {
    const newValue = value.replace(' MB', '');
    const bytes = parseFloat(newValue) * (1024 * 1024);
    return bytes.toFixed(0);
  } else if (value.endsWith(' KB')) {
    const newValue = value.replace(' KB', '');
    const bytes = parseFloat(newValue) * 1024;
    return bytes.toFixed(0);
  } else return value;
}

const TIME_UNITS = {
  year: 12 * 30 * 24 * 60 * 60 * 1000,
  month: 30 * 24 * 60 * 60 * 1000, // Approximating a month as 30 days
  week: 7 * 24 * 60 * 60 * 1000,
  day: 24 * 60 * 60 * 1000,
  hour: 60 * 60 * 1000,
  minute: 60 * 1000,
  second: 1000,
};

export function getLastActiveTime(dateTimeString: string, timeOnly?: boolean) {
  if (dateTimeString == null) return 'none';
  if (isNaN(new Date(dateTimeString).getTime())) return dateTimeString; //returns date if it cannot be converted to milliseconds
  const currentDate: Date = new Date();
  const lastActiveDate: Date = new Date(dateTimeString);

  const TIME_DIFFERENCE = currentDate.getTime() - lastActiveDate.getTime(); // Difference in milliseconds

  // Define the time units and their respective milliseconds

  // Calculate the difference in each time unit
  const years = Math.floor(TIME_DIFFERENCE / TIME_UNITS.year);
  const months = Math.floor(TIME_DIFFERENCE / TIME_UNITS.month);
  const weeks = Math.floor(
    (TIME_DIFFERENCE % TIME_UNITS.month) / TIME_UNITS.week,
  );
  const days = Math.floor(TIME_DIFFERENCE / TIME_UNITS.day);
  const hours = Math.floor(
    (TIME_DIFFERENCE % TIME_UNITS.day) / TIME_UNITS.hour,
  );
  const minutes = Math.floor(
    (TIME_DIFFERENCE % TIME_UNITS.hour) / TIME_UNITS.minute,
  );
  const seconds = Math.floor(
    (TIME_DIFFERENCE % TIME_UNITS.minute) / TIME_UNITS.second,
  );

  const timeCalcs = [years, months, weeks, days, hours, minutes, seconds];
  const timeLabels = Object.keys(TIME_UNITS);
  const timeIndex = timeCalcs.findIndex((unit) => unit > 0);

  // Construct the "last active" message based on the difference
  return `${timeOnly && 'Last Active '}${timeCalcs[timeIndex]} ${timeLabels[timeIndex]
    }${timeCalcs[timeIndex] > 1 ? 's' : ''} ago`;
}

export function isEmailValid(email: string) {
  const EMAIL_REGEX =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  if (email.match(EMAIL_REGEX)) return true;
  else return false;
}

export function setupRoles(role: string | undefined) {
  switch (role) {
    case 'Tenant Admin':
      return 'Admin';
    case 'Tenant':
      return 'User';
    default:
      return role;
  }
}

export const getMapValue = (value: number) => {
  switch (value) {
    case 1:
      return 'Pending';
    case 2:
      return 'Viewed';
    case 3:
      return 'Responded';
    case 4:
      return 'Closed';
    default:
      return 'Pending';
  }
};

export const getResponseStyle = (value: number) => {
  const initialStyle = {
    textAlign: 'center',
    fontFamily: 'Schibsted Grotesk',
    fontSize: '16px',
    fontStyle: 'normal',
    color: 'var(--ri-neutral-white-bg-900)',
    fontWeight: 400,
    marginRight: '16px',
  };

  if (value === 3 || value === 4) {
    return {
      ...initialStyle,
      textDecoration: 'underline',
    };
  }

  return {
    ...initialStyle,
    color: 'var(--ri-neutral-white-bg-300)',
  };
};

export const convertHours = (hours: number, showWholeValue = false) => {
  const units = [
    { name: 'd', value: 24 },
    { name: 'h', value: 1 },
    { name: 'm', value: 1 / 60 },
    { name: 's', value: 1 / 3600 },
  ];
  let remaining = hours;
  const result = [];
  for (const unit of units) {
    if (remaining >= unit.value) {
      const wholeUnits = Math.floor(remaining / unit.value);
      remaining %= unit.value;
      result.push(`${wholeUnits}${unit.name}`);
    }
  }

  if (showWholeValue) {
    return result.join(' ');
  } else {
    return result[0];
  }
};

export function isNotNull<T>(value: T | null): value is T {
  return value !== null;
}
